/**
 * Захват источника визита: utm-метки, реферер, код короткой ссылки.
 *
 * Храним два касания: first (первый визит — откуда человек узнал о нас)
 * и last (последний визит перед регистрацией). Данные складываются в
 * localStorage и уходят на бэкенд один раз — в теле запроса регистрации.
 */

const STORAGE_KEY = "anylex-attribution";
const VISITOR_KEY = "anylex-visitor-id";
// Код пригласившего из ?ref= — отдельно от меток: живёт до регистрации или первого входа через Яндекс/VK
const REFERRAL_KEY = "anylex-referral-code";
const REFERRAL_PARAM = "ref";
const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const CLICK_ID_PARAMS = ["gclid", "yclid", "ysclid", "fbclid"];
const LINK_PARAM = "alx_l";

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function readStore() {
  if (!isBrowser()) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeStore(value) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch (error) {
    /* приватный режим или заблокированное хранилище — атрибуция не критична */
  }
}

function getVisitorId() {
  try {
    const existing = window.localStorage.getItem(VISITOR_KEY);
    if (existing) {
      return existing;
    }
    const generated =
      typeof window.crypto?.randomUUID === "function"
        ? window.crypto.randomUUID()
        : `v${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(VISITOR_KEY, generated);
    return generated;
  } catch (error) {
    return null;
  }
}

function readTouchFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const touch = {};

  UTM_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      touch[key] = value.slice(0, 200);
    }
  });
  CLICK_ID_PARAMS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      touch[key] = value.slice(0, 200);
    }
  });

  const referrer = document.referrer || "";
  let externalReferrer = "";
  if (referrer) {
    try {
      if (new URL(referrer).hostname !== window.location.hostname) {
        externalReferrer = referrer.slice(0, 1000);
      }
    } catch (error) {
      externalReferrer = "";
    }
  }
  if (externalReferrer) {
    touch.referrer = externalReferrer;
  }

  touch.landing_path = `${window.location.pathname}${window.location.search}`.slice(0, 500);
  touch.at = new Date().toISOString();

  const hasSignal =
    UTM_PARAMS.some((key) => touch[key]) ||
    CLICK_ID_PARAMS.some((key) => touch[key]) ||
    Boolean(touch.referrer);

  return { touch, hasSignal, linkCode: (params.get(LINK_PARAM) || "").slice(0, 16) };
}

/**
 * Вызывается один раз при загрузке приложения. Пишет first-touch, если его ещё нет,
 * и обновляет last-touch, когда в URL есть новые метки.
 */
export function captureAttribution() {
  if (!isBrowser()) {
    return null;
  }

  const { touch, hasSignal, linkCode } = readTouchFromUrl();
  captureReferralCode();
  const stored = readStore() || {};
  const next = {
    visitor_id: stored.visitor_id || getVisitorId(),
    first: stored.first || touch,
    last: hasSignal ? touch : stored.last || stored.first || touch,
    link_code: linkCode || stored.link_code || null,
    first_seen_at: stored.first_seen_at || touch.at,
  };

  writeStore(next);
  return next;
}

/** Данные для отправки вместе с регистрацией. */
export function getAttributionPayload() {
  if (!isBrowser()) {
    return null;
  }
  return readStore() || captureAttribution();
}

function captureReferralCode() {
  try {
    const code = (new URLSearchParams(window.location.search).get(REFERRAL_PARAM) || "").trim().slice(0, 50);
    if (code) {
      window.localStorage.setItem(REFERRAL_KEY, code);
    }
  } catch (error) {
    /* хранилище недоступно — привязка по ссылке не сработает, регистрация не страдает */
  }
}

/** Код пригласившего, пришедший по ссылке ?ref=. */
export function getReferralCode() {
  if (!isBrowser()) {
    return "";
  }
  try {
    return window.localStorage.getItem(REFERRAL_KEY) || "";
  } catch (error) {
    return "";
  }
}

export function clearReferralCode() {
  try {
    window.localStorage.removeItem(REFERRAL_KEY);
  } catch (error) {
    /* ignore */
  }
}

/**
 * Вход через Яндекс/VK минует форму регистрации — привязываем по сохранённому коду
 * после входа. Бэкенд сам отказывает не новым аккаунтам; код используется один раз.
 */
export async function claimPendingReferral(request, accessToken) {
  const code = getReferralCode();
  if (!code) {
    return;
  }
  try {
    await request("/api/v1/referrals/attach", {
      method: "POST",
      body: JSON.stringify({ code }),
      headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    });
  } catch (error) {
    /* непривязка не мешает входу */
  } finally {
    clearReferralCode();
  }
}

/** После успешной регистрации метки больше не нужны. */
export function clearAttribution() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    /* ignore */
  }
}
